import React, { useRef } from 'react';

import { Plus } from 'lucide-react';

interface AnswerEditorSimpleProps {
  answer: string;
  onChange: (value: string) => void;
  onInsertCitation: (marker: string) => void;
  citationNumbers: string[];
}

const AnswerEditorSimple: React.FC<AnswerEditorSimpleProps> = ({
  answer,
  onChange,
  onInsertCitation,
  citationNumbers,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleInsertCitation = () => {
    // Calculate next citation number
    const maxNumber = citationNumbers.length > 0 ? Math.max(...citationNumbers.map(Number)) : 0;
    const nextNumber = maxNumber + 1;
    const marker = `[${nextNumber}]`;

    // Insert at cursor position
    if (textareaRef.current) {
      const start = textareaRef.current.selectionStart;
      const end = textareaRef.current.selectionEnd;
      const newValue = answer.substring(0, start) + marker + answer.substring(end);
      onChange(newValue);

      // Set cursor position after inserted marker
      setTimeout(() => {
        textareaRef.current?.focus();
        const newPosition = start + marker.length;
        textareaRef.current?.setSelectionRange(newPosition, newPosition);
      }, 0);
    } else {
      onInsertCitation(marker);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="mb-2 flex items-center justify-between">
        <label htmlFor="answer-input" className="text-sm font-semibold text-gray-700">
          Answer
        </label>
        <button
          type="button"
          onClick={handleInsertCitation}
          className="flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-blue-700"
        >
          <Plus className="size-3" />
          Add Citation
        </button>
      </div>
      <textarea
        id="answer-input"
        ref={textareaRef}
        value={answer}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter your answer here with citations like [1], [2], etc..."
        className="h-full w-full resize-none rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
      />
    </div>
  );
};

export default AnswerEditorSimple;
